import React, { Fragment, useMemo, useState } from "react";
import ProtobufPart from "./ProtobufPart";
import { Table, Button, Icon, Segment, Header, Label } from "semantic-ui-react";
import { bufferToPrettyHex } from "./hexUtils";
import { TYPES } from "./protobufDecoder";
import { useI18n } from "./i18n";

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return true;
  }
};

function splitDelimitedPartsInMessages(parts) {
  var messages = [];
  var currentMessage = [];
  for (var i = 0; i < parts.length; i++) {
    if (
      parts[i].type === TYPES.MSG_LEN_DELIMITER &&
      currentMessage.length > 0
    ) {
      messages.push(currentMessage);
      currentMessage = [parts[i]];
    } else {
      currentMessage.push(parts[i]);
    }
  }
  if (currentMessage.length > 0) {
    messages.push(currentMessage);
  }
  return messages;
}

function formatByteRange(byteRange) {
  if (!byteRange || byteRange.length < 2) return "N/A";
  const start = byteRange[0];
  const end = byteRange[1];
  const length = end - start;
  
  if (length === 0) return `${start}`;
  if (length === 1) return `${start}`;
  return `${start}-${end-1} (${length} bytes)`;
}

function formatFieldNumber(index) {
  if (index === -1) return "Message Delimiter";
  if (index === 0) return "Unknown";
  return `${index}`;
}

function generateMessageSourceDataNew(messageParts, originalData) {
  if (!originalData || !originalData.length) {
    return '';
  }
  
  let messageStart = Infinity;
  let messageEnd = 0;
  
  for (const part of messageParts) {
    if (part.byteRange && part.byteRange.length >= 2) {
      messageStart = Math.min(messageStart, part.byteRange[0]);
      messageEnd = Math.max(messageEnd, part.byteRange[1]);
    }
  }
  
  if (messageStart === Infinity || messageEnd === 0) {
    return '';
  }
  
  let includeLengthPrefix = false;
  let lengthPrefixStart = messageStart;
  let lengthPrefixEnd = messageStart;
  
  if (messageParts.length > 0 && messageParts[0].type === TYPES.MSG_LEN_DELIMITER) {
    includeLengthPrefix = true;
    lengthPrefixEnd = messageParts[0].byteRange[1];
  }
  
  const finalStart = includeLengthPrefix ? lengthPrefixStart : messageStart;
  const finalEnd = messageEnd;
  
  const messageBytes = originalData.slice(finalStart, finalEnd);
  
  const hexString = Array.from(messageBytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join(' ');
  
  return hexString;
}

function generateMessageSourceData(messageParts, originalData) {
  if (!originalData || !originalData.length) {
    return '';
  }
  
  let messageStart = Infinity;
  let messageEnd = 0;
  
  for (const part of messageParts) {
    if (part.byteRange && part.byteRange.length >= 2) {
      messageStart = Math.min(messageStart, part.byteRange[0]);
      messageEnd = Math.max(messageEnd, part.byteRange[1]);
    }
  }
  
  if (messageStart === Infinity || messageEnd === 0) {
    return '';
  }
  
  let includeLengthPrefix = false;
  let lengthPrefixStart = messageStart;
  let lengthPrefixEnd = messageStart;
  
  if (messageParts.length > 0 && messageParts[0].type === TYPES.MSG_LEN_DELIMITER) {
    includeLengthPrefix = true;
    lengthPrefixEnd = messageParts[0].byteRange[1];
  }
  
  const finalStart = includeLengthPrefix ? lengthPrefixStart : messageStart;
  const finalEnd = messageEnd;
  
  const messageBytes = originalData.slice(finalStart, finalEnd);
  
  const hexString = Array.from(messageBytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join(' ');
  
  return hexString;
}

const showCopySuccess = (message) => {
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #4CAF50;
    color: white;
    padding: 12px 20px;
    border-radius: 4px;
    z-index: 1000;
    font-family: Arial, sans-serif;
    font-size: 14px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    transition: opacity 0.3s ease;
  `;
  notification.textContent = message || 'Copied!';
  document.body.appendChild(notification);
  
  setTimeout(() => {
    notification.style.opacity = '0';
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 300);
  }, 3000);
};

function LargeDataContent({ data, type }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxDisplayLength = 100;
  
  if (type === "bytes" && data.length > maxDisplayLength) {
    const preview = data.substring(0, maxDisplayLength);
    const totalBytes = data.split(' ').length;
    
    return (
      <div>
        <div style={{ marginBottom: '8px' }}>
          <Label color="blue" size="small">
            <Icon name="database" />
            {totalBytes} bytes
          </Label>
          <Button 
            size="mini" 
            compact 
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ marginLeft: '8px' }}
          >
            <Icon name={isExpanded ? 'compress' : 'expand'} />
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
        <div style={{ 
          fontFamily: 'monospace', 
          fontSize: '11px',
          backgroundColor: '#f8f9fa',
          padding: '12px',
          borderRadius: '6px',
          border: '1px solid #e9ecef',
          maxHeight: isExpanded ? 'none' : '200px',
          overflow: isExpanded ? 'visible' : 'auto',
          lineHeight: '1.4'
        }}>
          {isExpanded ? data : preview + '...'}
        </div>
      </div>
    );
  }
  
  return (
    <div style={{ 
      fontFamily: 'monospace', 
      fontSize: '11px',
      backgroundColor: '#f8f9fa',
      padding: '12px',
      borderRadius: '6px',
      border: '1px solid #e9ecef',
      lineHeight: '1.4'
    }}>
      {data}
    </div>
  );
}

function HexDataDisplay({ hexData, byteRange }) {
  const [showAsTable, setShowAsTable] = useState(false);
  
  const bytes = hexData.split(' ').filter(b => b.trim());
  const totalBytes = bytes.length;
  
  const formatHexTable = (bytes) => {
    const rows = [];
    for (let i = 0; i < bytes.length; i += 16) {
      const row = bytes.slice(i, i + 16);
      const offset = i;
      const hex = row.map(b => b.padStart(2, '0')).join(' ');
      const ascii = row.map(b => {
        const charCode = parseInt(b, 16);
        return (charCode >= 32 && charCode <= 126) ? String.fromCharCode(charCode) : '.';
      }).join('');
      
      rows.push({ offset, hex, ascii });
    }
    return rows;
  };

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '12px'
      }}>
        <div>
          <Label color="teal" size="small">
            <Icon name="code" />
            {totalBytes} bytes
          </Label>
          <Label color="grey" size="small">
            {formatByteRange(byteRange)}
          </Label>
        </div>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          <Button 
            size="mini" 
            compact 
            onClick={() => setShowAsTable(!showAsTable)}
          >
            <Icon name={showAsTable ? 'list' : 'table'} />
            {showAsTable ? 'List' : 'Table'}
          </Button>
        </div>
      </div>
      
      {showAsTable ? (
        <div style={{ 
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          border: '1px solid #e9ecef',
          overflow: 'auto'
        }}>
          <Table celled compact size="small" style={{ margin: 0, width: '100%' }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={{ width: '15%', minWidth: '80px' }}>Offset</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '70%', minWidth: '200px' }}>Hex</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '15%', minWidth: '80px' }}>ASCII</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {formatHexTable(bytes).map((row, i) => (
                <Table.Row key={i}>
                  <Table.Cell style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-word' }}>
                    {row.offset.toString(16).padStart(4, '0').toUpperCase()}
                  </Table.Cell>
                  <Table.Cell style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>
                    {row.hex}
                  </Table.Cell>
                  <Table.Cell style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-word' }}>
                    {row.ascii}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ) : (
        <div style={{ 
          fontFamily: 'monospace', 
          fontSize: '11px',
          backgroundColor: '#f8f9fa',
          padding: '12px',
          borderRadius: '6px',
          border: '1px solid #e9ecef',
          lineHeight: '1.4',
          overflow: 'auto'
        }}>
          {hexData}
        </div>
      )}
    </div>
  );
}

function ProtobufDisplay(props) {
  const { value, originalData } = props;
  const { t } = useI18n();

  const { messages, messageElements, parts, leftOver, totalBytes } = useMemo(() => {
    const messages = splitDelimitedPartsInMessages(value.parts);
    const totalBytes = value.parts.reduce((sum, part) => {
      return sum + (part.byteRange[1] - part.byteRange[0]);
    }, 0);
    
    const messageElements = messages.map((messageParts, i) => {
      const messageBytes = messageParts.reduce((sum, part) => {
        return sum + (part.byteRange[1] - part.byteRange[0]);
      }, 0);
      
      return {
        parts: messageParts,
        index: i,
        bytes: messageBytes
      };
    });

    const parts = value.parts.map((part, i) => {
      return <ProtobufPart key={i} part={part} originalData={originalData} />;
    });

    const leftOver = value.leftOver.length ? (
      <p>{t("left_over_bytes", { hex: bufferToPrettyHex(value.leftOver) })}</p>
    ) : null;

    return { messages, messageElements, parts, leftOver, totalBytes };
  }, [value, originalData, t]);

  return (
    <Fragment>
      <Segment>
        <Header as="h4" style={{ marginBottom: '16px' }}>
          <Icon name="info circle" />
          <Header.Content>
            {t("protobuf_decode_result")}
            <Header.Subheader>
              {t("protobuf_total", { bytes: totalBytes, messages: messages.length })}
            </Header.Subheader>
          </Header.Content>
        </Header>
        
        {messageElements.map((message, i) => (
          <Segment key={`message-${i}`} style={{ marginBottom: '16px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <Header as="h5" style={{ margin: 0 }}>
                {t("protobuf_message", { index: i + 1, bytes: message.bytes })}
              </Header>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button 
                  size="mini" 
                  compact 
                  color="blue"
                  onClick={async () => {
                    const sourceData = generateMessageSourceDataNew(message.parts, originalData);
                    if (sourceData) {
                      const success = await copyToClipboard(sourceData);
                      if (success) {
                        showCopySuccess(t("copied_message_source"));
                      }
                    }
                  }}
                >
                  <Icon name="copy" />
                  {t("copy_source")}
                </Button>
              </div>
            </div>
            
            <div style={{ 
              width: '100%',
              overflowX: 'auto',
              border: '1px solid #e1e8ed',
              borderRadius: '4px'
            }}>
              <Table celled compact size="small" style={{ margin: 0, minWidth: '600px', width: '100%' }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{ width: '12%', minWidth: '75px' }}>Byte Range</Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '12%', minWidth: '70px' }}>Field Number</Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '12%', minWidth: '70px' }}>Type</Table.HeaderCell>
                    <Table.HeaderCell style={{ width: '64%', minWidth: '50px' }}>Content</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {message.parts.map((part, j) => (
                    <ProtobufPart key={j} part={part} originalData={originalData} />
                  ))}
                </Table.Body>
              </Table>
            </div>
          </Segment>
        ))}
      </Segment>
      
      {leftOver}
    </Fragment>
  );
}

export default React.memo(ProtobufDisplay);
